import { endJobAssignmentReasons } from '../EndJobAssignmentModal/structure'
import { fetchCancellableBookings, fetchShiftOverbookCover } from 'syft-acp-core/api/resources/bookings'

import { CancelReason, CancelStep, ShiftOverbookCover, SpotDecision, WorkerJobBooking } from './types'

export const MODAL_NAME = 'bulkCancelBookingModal'
export const OTHER_LABELS = ['Other - Involuntary', 'Other - Voluntary']
export const WINDOW_24H_MS = 24 * 60 * 60 * 1000

export const isOtherReason = (reason: CancelReason) =>
  typeof reason === 'string' && OTHER_LABELS.includes(reason)

export const startMsOf = (b: WorkerJobBooking) =>
  typeof b.start_time === 'number' ? b.start_time : Date.parse(b.start_time)

// Human-readable label for the picked reason: dropdown ids resolve through
// endJobAssignmentReasons, anything already textual (or null) passes through.
export const reasonLabelFor = (reason: CancelReason) =>
  typeof reason === 'number' ? endJobAssignmentReasons.find(r => r.id === reason)?.label : reason

export const toggleInList = (ids: number[], id: number) =>
  ids.includes(id) ? ids.filter(existing => existing !== id) : [...ids, id]

// Does the given selection include a shift with FC or CH set? Drives the "last chance to
// configure flags" warning — shown only when it's actually relevant.
export const hasFlaggedShift = (bookings: WorkerJobBooking[], selected: number[]) =>
  bookings.some(
    b => selected.includes(b.shift_id) && (b.syft_compensation || b.syft_unpaid_hours_compensation),
  )

// Selected shifts with a known overbook cover result, paired with their booking —
// covered and uncovered alike, so the preview can say "no cover" as plainly as
// "X is first in line" rather than only surfacing the good-news case.
export const buildCoverPreview = (
  selected: number[],
  bookings: WorkerJobBooking[],
  overbookCover: Record<number, ShiftOverbookCover>,
) =>
  selected
    .map(shiftId => {
      const booking = bookings.find(b => b.shift_id === shiftId)
      const cover = overbookCover[shiftId]

      return booking && cover ? { booking, cover } : null
    })
    .filter((x): x is { booking: WorkerJobBooking; cover: ShiftOverbookCover } => x !== null)

// The cancel request body. Only attaches the free-text custom reason when the selected
// reason is actually an "Other" option — otherwise a stale value (typed, then switched
// away from "Other") would leak into the payload.
export const buildCancelPayload = ({
  workerID,
  jobID,
  shiftIDs,
  currentShiftID,
  reason,
  reasonLabel,
  otherReason,
}: {
  workerID?: number
  jobID?: number
  shiftIDs: number[]
  currentShiftID?: number
  reason: CancelReason
  reasonLabel?: string | null
  otherReason?: string
}) => ({
  worker_id: workerID,
  job_id: jobID,
  shift_ids: [...shiftIDs],
  // shift_id scopes the post-cancel bookings refresh to the table's current shift.
  shift_id: currentShiftID,
  user_provided_reason: reasonLabel ?? reason,
  user_provided_custom_reason: isOtherReason(reasonLabel ?? reason) ? otherReason || null : null,
})

// Loads the worker's cancellable bookings for the dialog's current target and pre-ticks
// the clicked shift, returning the effect cleanup (or undefined when there is nothing to
// load). Module level so the container's own logic stays readable; all state still lives
// in the container.
export const loadCancellableBookings = ({
  isShown,
  workerID,
  jobID,
  currentShiftID,
  resetDecisions,
  setBookings,
  setSelected,
  setLoading,
  setLoadError,
}: {
  isShown: boolean
  workerID?: number
  jobID?: number
  currentShiftID?: number
  resetDecisions: () => void
  setBookings: (bookings: WorkerJobBooking[]) => void
  setSelected: (shiftIDs: number[]) => void
  setLoading: (loading: boolean) => void
  setLoadError: (error?: string) => void
}) => {
  if (!isShown || workerID == null || jobID == null) {
    return undefined
  }
  let cancelled = false
  // Every decision was taken against the previous target, so none of them survive it.
  resetDecisions()
  setLoading(true)
  setLoadError(undefined)
  fetchCancellableBookings({ workerID, jobID })
    .then((res: { payload: WorkerJobBooking[] }) => {
      if (cancelled) return
      // handleCall resolves an envelope { payload, response, meta } — the bookings
      // array is on `payload` (see any direct api/resources consumer, e.g. DataEmployerVenue).
      const list = Array.isArray(res?.payload) ? res.payload : []
      setBookings(list)
      // Pre-tick the clicked shift if the worker is still booked on it.
      const match = list.find(b => Number(b.shift_id) === Number(currentShiftID))
      setSelected(match ? [match.shift_id] : [])
      setLoading(false)
    })
    .catch(() => {
      if (cancelled) return
      setLoadError('Could not load this worker’s bookings. Please close and try again.')
      setLoading(false)
    })

  return () => {
    cancelled = true
  }
}

// Refreshes the overbook-cover preview for the current selection. Any prior result is
// dropped up front — otherwise, while this request is in flight, a stale cover for a
// shift still in the new selection would keep rendering as if it were current.
// Best-effort: a failure here just shows nothing rather than blocking the cancellation
// the preview is describing.
export const loadOverbookCover = ({
  isShown,
  shiftIDs,
  setOverbookCover,
}: {
  isShown: boolean
  shiftIDs: number[]
  setOverbookCover: (cover: Record<number, ShiftOverbookCover>) => void
}) => {
  setOverbookCover({})
  if (!isShown || shiftIDs.length === 0) {
    return undefined
  }
  let cancelled = false
  fetchShiftOverbookCover({ shiftIDs })
    .then((res: { payload: ShiftOverbookCover[] }) => {
      if (cancelled) return
      const list = Array.isArray(res?.payload) ? res.payload : []
      setOverbookCover(Object.fromEntries(list.map(c => [c.shift_id, c])))
    })
    .catch(() => {
      if (!cancelled) setOverbookCover({})
    })

  return () => {
    cancelled = true
  }
}

// Footer submit label for the current step, retry state and spot decision.
export const submitLabel = ({
  step,
  count,
  spotDecision,
  failedSpotCount,
}: {
  step: CancelStep
  count: number
  spotDecision: SpotDecision
  failedSpotCount: number
}) => {
  if (step === 'select') {
    return 'Next'
  }
  if (failedSpotCount > 0) {
    return 'Retry removing spot(s)'
  }
  if (spotDecision === 'delete') {
    return `Cancel & remove ${count === 1 ? 'spot' : 'spots'}`
  }

  return `Cancel ${count} ${count === 1 ? 'shift' : 'shifts'}`
}
